import noop from 'lodash/noop';
import { useCallback, useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileVersion } from '~/shared/data-contract/job-profile-versions';

import { type UrlTileContextValue, useOptionalUrlTiles } from '../../url-tiles';
import { useTrackProfileTaskInteraction } from './analytics/track-profile-task-interaction';
import {
    getProfileTasksAvailability,
    type JobProfileTaskTransport,
    type ProfileTasksUpdateTransport,
} from './api/profile-task-transport-adapter';
import { useProfileTaskDraftCoordinator } from './draft/ProfileTaskDraftCoordinatorProvider';
import type { ProfileTaskDraftCoordinator } from './draft/profile-task-draft-coordinator';
import {
    commitProfileTaskSave,
    type PreparedProfileTaskSave,
    prepareProfileTaskSave,
    preserveProfileTaskSaveFailure,
} from './draft/profile-task-save-lifecycle';
import { remapPersistedProfileTasks } from './draft/remap-persisted-profile-tasks';
import type { ProfileTaskParentContext, ProfileTaskSummary } from './model/profile-task';
import { JSA_PROFILE_TASK_DETAIL_TILE_TYPE } from './tile/profile-task-tile-types';
import { createPersistedProfileTaskTilePayload, profileTaskTileSchema } from './tile/task-tile-url';

type ReadyProfileTaskSave = Extract<PreparedProfileTaskSave, { readonly status: 'ready' }>;

export type ProfileTaskSaveHandle =
    | { readonly blockedMessage: string; readonly canProceed: false }
    | {
          readonly canProceed: true;
          readonly removedTaskIds: readonly string[];
          readonly reportFailure: (completedRemovalIds?: readonly string[]) => void;
          readonly reportSuccess: (
              tasks: readonly JobProfileTaskTransport[] | undefined,
              latestVersion: JobProfileVersion | undefined,
          ) => void;
          readonly tasks: ProfileTasksUpdateTransport | undefined;
      };

type UseProfileTaskFormActionsParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly itemVersion: number | undefined;
    readonly parentRoleId?: string;
    readonly selectedVersion: string | undefined;
};

type UseProfileTaskFormActionsResult = {
    readonly beginSave: () => ProfileTaskSaveHandle;
};

const remapOpenProfileTaskTiles = ({
    architectureId,
    context,
    parentVersion,
    remappings,
    urlTiles,
}: {
    readonly architectureId: string;
    readonly context: ProfileTaskParentContext;
    readonly parentVersion: number;
    readonly remappings: ReturnType<typeof remapPersistedProfileTasks>;
    readonly urlTiles: UrlTileContextValue | null;
}): void => {
    for (const remapping of remappings) {
        const openTile = urlTiles?.tiles.find(
            ({ payload }) =>
                payload.type === JSA_PROFILE_TASK_DETAIL_TILE_TYPE && payload['taskId'] === remapping.previousTaskId,
        );
        const parsedOpenTile = profileTaskTileSchema.safeParse(openTile?.payload);
        if (!parsedOpenTile.success) {
            continue;
        }
        const openPayload = parsedOpenTile.data;
        const persistedPayload = createPersistedProfileTaskTilePayload({
            architectureId,
            isCommentsOpen: openPayload.isCommentsOpen,
            language: openPayload.language,
            parentId: context.parentId,
            parentType: context.parentType,
            parentVersion,
            roleId: context.roleId,
            taskId: remapping.canonicalTaskId,
            taskVersionId: remapping.canonicalTaskVersionId,
        });
        if (remapping.previousTaskId === remapping.canonicalTaskId) {
            urlTiles?.updateTile(persistedPayload);
            continue;
        }
        urlTiles?.openTile(persistedPayload);
    }
};

const completeProfileTaskSave = ({
    architectureId,
    canonicalTasks,
    context,
    coordinator,
    latestVersion,
    prepared,
    urlTiles,
}: {
    readonly architectureId: string;
    readonly canonicalTasks: readonly ProfileTaskSummary[];
    readonly context: ProfileTaskParentContext;
    readonly coordinator: ProfileTaskDraftCoordinator;
    readonly latestVersion: JobProfileVersion;
    readonly prepared: ReadyProfileTaskSave;
    readonly urlTiles: UrlTileContextValue | null;
}): void => {
    remapOpenProfileTaskTiles({
        architectureId,
        context,
        parentVersion: latestVersion.version,
        remappings: remapPersistedProfileTasks(prepared.ordered, canonicalTasks),
        urlTiles,
    });
    commitProfileTaskSave(coordinator, context, latestVersion.version, canonicalTasks);
};

export const useProfileTaskFormActions = ({
    architectureId,
    itemId,
    itemType,
    itemVersion,
    parentRoleId,
    selectedVersion,
}: UseProfileTaskFormActionsParams): UseProfileTaskFormActionsResult => {
    const translate = useTranslate();
    const coordinator = useProfileTaskDraftCoordinator();
    const urlTiles = useOptionalUrlTiles();
    const context = useMemo<ProfileTaskParentContext>(
        () => ({
            architectureId,
            parentId: itemId,
            parentType: itemType === 'job-role' ? 'role' : 'level',
            parentVersion: Number(selectedVersion ?? itemVersion ?? 1),
            roleId: itemType === 'job-level' ? parentRoleId : undefined,
        }),
        [architectureId, itemId, itemType, itemVersion, parentRoleId, selectedVersion],
    );
    const trackInteraction = useTrackProfileTaskInteraction(context);

    const beginSave = useCallback((): ProfileTaskSaveHandle => {
        const prepared = prepareProfileTaskSave(coordinator, context);
        if (prepared.status === 'blocked') {
            return {
                blockedMessage: translate(
                    'pages.job-architecture-v2.profile-tasks.validation.complete-tasks-before-parent-save',
                    { count: prepared.blockedTaskIds.length },
                ),
                canProceed: false,
            };
        }
        if (prepared.status === 'absent') {
            return {
                canProceed: true,
                removedTaskIds: [],
                reportFailure: noop,
                reportSuccess: noop,
                tasks: undefined,
            };
        }
        return {
            canProceed: true,
            removedTaskIds: prepared.removedTaskIds,
            reportFailure: (completedRemovalIds) => {
                preserveProfileTaskSaveFailure(coordinator, context, prepared, completedRemovalIds);
                trackInteraction({ action: 'parent-save-failed', affectedTaskCount: prepared.affectedTaskCount });
            },
            reportSuccess: (tasks, latestVersion) => {
                const availability = getProfileTasksAvailability(tasks);
                if (availability.status === 'available' && latestVersion !== undefined) {
                    completeProfileTaskSave({
                        architectureId,
                        canonicalTasks: availability.summaries,
                        context,
                        coordinator,
                        latestVersion,
                        prepared,
                        urlTiles,
                    });
                }
                trackInteraction({ action: 'parent-save-succeeded', affectedTaskCount: prepared.affectedTaskCount });
            },
            tasks: prepared.tasks,
        };
    }, [architectureId, context, coordinator, trackInteraction, translate, urlTiles]);

    return { beginSave };
};
